import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAvailableLocales } from 'models/localization';

import { UserSessionContext } from 'components/UserSession';
import Routes from 'Routes';
import HelpButton from './HelpButton';
import LocaleSelector from './LocaleSelector';

import DrawerContainer from './mobile/DrawerContainer';
import SearchButtonContainer from './mobile/SearchButtonContainer';

const RootContainer = styled.div`
  width: 100%;
  z-index: 1000;
`;

const useAppBarStyles = makeStyles(() => ({
  root: {
    zIndex: 1000,
  },
  colorPrimary: {
    color: '#484848',
    backgroundColor: '#FFF',
  },
}));

const StyledToolbar = withStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
}))(Toolbar);

const StyledLogoContainer = styled.div`
  flex-grow: 1;
  display: flex;
  max-width: 100%;
`;

const StyledLink = styled(Link)`
  display: inline-flex;
`;

const LogoImage = styled.img`
  display: block;
  max-width: 100%;
  max-height: ${({ logoHeight }) => logoHeight}px;

  &:not(:last-child) {
    margin-right: 8px;
  }
`;

export default function MobileAppBar({
  logos,
  hideDrawer,
  hideSearchBar,
}) {
  const appBarClasses = useAppBarStyles();
  const { canSee, firstVisibleRoute } = useContext(UserSessionContext);
  const availableLocales = useSelector(selectAvailableLocales);

  return (
    <>
      <RootContainer>
        <AppBar
          position="static"
          className={`${appBarClasses.root} ${appBarClasses.colorPrimary}`}
          elevation={0}
        >
          <StyledToolbar>
            {!hideDrawer && <DrawerContainer />}
            <StyledLogoContainer>
              {logos.map((logo) => {
                const key = `${logo.src}-${logo.redirectTo}`;
                const to = logo.redirectUrl || Routes[firstVisibleRoute];
                const child = <LogoImage logoHeight={logo.height} src={logo.src} alt="Event Logo" />;
                if (logo.redirectExternal) return <a key={key} href={to} target="_blank" rel="noopener noreferrer">{child}</a>;
                return <StyledLink key={key} to={to}>{child}</StyledLink>;
              })}
            </StyledLogoContainer>
            {
              !hideSearchBar && canSee('search') && <SearchButtonContainer />
            }
            {availableLocales.length > 1 && <LocaleSelector />}
            {!canSee('lobby') && <div><HelpButton /></div>}
          </StyledToolbar>
        </AppBar>
      </RootContainer>
    </>
  );
}

MobileAppBar.propTypes = {
  logos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  hideSearchBar: PropTypes.bool,
  hideDrawer: PropTypes.bool,
};

MobileAppBar.defaultProps = {
  hideSearchBar: false,
  hideDrawer: false,
};
